import { apiFetchJson } from '../apiClient';

const API_BASE = '/api/funding-hq';

export const FUNDING_SESSION_EXPIRED_MESSAGE =
  'Your session has expired. Please sign in again to continue.';

export const FUNDING_FORBIDDEN_MESSAGE =
  'You do not have permission to perform this action.';

export const CREDENTIAL_DECRYPT_FAILED_CODE = 'CREDENTIAL_DECRYPT_FAILED';

const CREDENTIAL_DECRYPT_FIELD_MESSAGES = {
  ssn: 'This saved SSN can no longer be decrypted. Re-enter the SSN and save again.',
  ein: 'This saved EIN can no longer be decrypted. Re-enter the EIN and save again.',
  application_password_created:
    'This saved application password can no longer be decrypted. Re-enter the application password and save again.',
};

export function isFundingAuthError(error) {
  return error?.status === 401;
}

export function isFundingForbiddenError(error) {
  return error?.status === 403;
}

export function getCredentialDecryptErrorCode(error) {
  return error?.code || error?.data?.code || null;
}

export function getCredentialDecryptErrorField(error) {
  return error?.field || error?.data?.field || null;
}

export function isCredentialDecryptFailed(error) {
  return getCredentialDecryptErrorCode(error) === CREDENTIAL_DECRYPT_FAILED_CODE;
}

export function getCredentialDecryptRecoveryMessage(field) {
  return (
    CREDENTIAL_DECRYPT_FIELD_MESSAGES[field]
    || 'This saved value can no longer be decrypted. Re-enter it and save again.'
  );
}

function formatValidationDetail(detail) {
  if (detail == null || detail === '') return '';
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail)) {
    return detail
      .map((item) => {
        if (typeof item === 'string') return item;
        if (item && typeof item === 'object') {
          const loc = Array.isArray(item.loc) ? item.loc.filter((part) => part !== 'body').join('.') : '';
          const msg = item.msg || item.message || JSON.stringify(item);
          return loc ? `${loc}: ${msg}` : msg;
        }
        return String(item);
      })
      .filter(Boolean)
      .join('; ');
  }
  if (typeof detail === 'object') {
    return Object.entries(detail)
      .map(([key, value]) => {
        if (Array.isArray(value)) return `${key}: ${value.join(', ')}`;
        if (value && typeof value === 'object') return `${key}: ${formatValidationDetail(value)}`;
        return `${key}: ${value}`;
      })
      .join('; ');
  }
  return String(detail);
}

export function getFundingApiErrorMessage(error, fallback = 'Request failed') {
  if (isFundingAuthError(error)) {
    return FUNDING_SESSION_EXPIRED_MESSAGE;
  }
  if (isFundingForbiddenError(error)) {
    const detailMessage = formatValidationDetail(error?.data?.detail);
    return detailMessage || FUNDING_FORBIDDEN_MESSAGE;
  }

  if (isCredentialDecryptFailed(error)) {
    return getCredentialDecryptRecoveryMessage(getCredentialDecryptErrorField(error));
  }

  const detailMessage = formatValidationDetail(error?.data?.detail);
  if (detailMessage) return detailMessage;

  const dataMessage = formatValidationDetail(error?.data?.message || error?.data?.error);
  if (dataMessage) return dataMessage;

  if (typeof error?.message === 'string' && error.message && error.message !== '[object Object]') {
    return error.message;
  }

  return fallback;
}

export function fundingHqFetch(path, options = {}) {
  const normalized = path.startsWith('/') ? path : `/${path}`;
  return apiFetchJson(`${API_BASE}${normalized}`, options);
}
